import { ClinicWidgetRequestType, FormTemplate, FormTemplateType } from 'shared/types/graphql';

export enum DisabledReasons {
  ServiceReminder = 'ServiceReminder',
  WebsiteWidget = 'Website Widget',
  ServiceReminderAndWebsiteWidget = 'ServiceReminder & WebsiteWidget',
  Automations = 'Automations',
}

type GetFormDeleteDisabledMessagesArgs = {
  formTemplateType: FormTemplate['formTemplateType'];
  hasLinkedAutomations: boolean;
  clinicWidgetRequestTypes: Partial<ClinicWidgetRequestType>[];
  includeReason?: boolean;
};

export type GetFormDeleteDisabledMessagesResult = { delete: string; archive: string };
export type GetFormDeleteDisabledMessagesResultWithReason = GetFormDeleteDisabledMessagesResult & {
  reason?: DisabledReasons;
};

type GetFormDeleteDisabledMessagesReturnTypes =
  | GetFormDeleteDisabledMessagesResult
  | GetFormDeleteDisabledMessagesResultWithReason;

export default function getFormDeleteDisabledMessages<T extends GetFormDeleteDisabledMessagesReturnTypes>({
  formTemplateType,
  clinicWidgetRequestTypes,
  hasLinkedAutomations,
  includeReason,
}: GetFormDeleteDisabledMessagesArgs): T {
  const res = {
    delete: '',
    archive: '',
    reason: undefined,
  } as GetFormDeleteDisabledMessagesResultWithReason;

  if (formTemplateType === FormTemplateType.ServiceReminder && Boolean(clinicWidgetRequestTypes?.length)) {
    res.delete =
      "This form can't be deleted because it's being used by Service Reminders and at least one Website Widget request option.";
    res.archive =
      "This form can't be archived because it's being used by Service Reminders and at least one Website Widget request option.";
    res.reason = DisabledReasons.ServiceReminderAndWebsiteWidget;
  } else if (formTemplateType === FormTemplateType.ServiceReminder) {
    res.delete = "This form can't be deleted because it's being used by Service Reminders.";
    res.archive = "This form can't be archived because it's being used by Service Reminders.";
    res.reason = DisabledReasons.ServiceReminder;
  } else if (Boolean(clinicWidgetRequestTypes?.length)) {
    res.delete = "This form can't be deleted because it's being used by at least one Website Widget request option.";
    res.archive = "This form can't be archived because it's being used by at least one Website Widget request option.";
    res.reason = DisabledReasons.WebsiteWidget;
  } else if (hasLinkedAutomations) {
    res.delete = "This form can't be deleted because it's being used by at least one Automation.";
    res.archive = "This form can't be archived because it's being used by at least one Automation.";
    res.reason = DisabledReasons.Automations;
  }

  if (!includeReason) {
    delete res.reason;
  }

  return res as T;
}

type GetComponentDisabledPropsArgs = {
  isActive?: boolean | null;
  disabledMessages: GetFormDeleteDisabledMessagesResultWithReason;
};

export const getComponentDisabledProps = ({
  isActive,
  disabledMessages,
}: GetComponentDisabledPropsArgs): { isDisabled: boolean; disabledMessage: string } => {
  // Revised condition for the following scenario:
  // Before Jan 26, 2024, archived forms could be used for website widgets,
  // which kept them archived and prevented users from unarchiving them.
  if (Boolean(disabledMessages.archive)) {
    if (isActive === false && disabledMessages.reason === DisabledReasons.WebsiteWidget) {
      return {
        isDisabled: false,
        disabledMessage: '',
      };
    }
    return {
      isDisabled: true,
      disabledMessage: disabledMessages.archive,
    };
  }

  return {
    isDisabled: false,
    disabledMessage: '',
  };
};
